import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsService } from '@/services/analytics/analyticsService';
import { ROUTES } from '@/app/router/routes';
import { AppFooter } from '@/components/layout/AppFooter';
import { OnboardingPanel } from './OnboardingPanel';
import styles from './AuthLayout.module.css';

/**
 * AuthLayout — the frame of the login, sign-up and MFA pages, as in Flutter: no header, the
 * onboarding panel (WebOnboardingPage) and the white form column side by side, each half the
 * width above 1080px, then the footer (WebPageFooter). On mobile only the form column shows.
 *
 * Between 768px and 1080px Flutter keeps both columns at full width in a horizontal scroll, so
 * the form starts off screen. React stacks them instead, the panel above the form (see
 * doc/FLUTTER_DEVIATIONS.md, "Auth pages between 768px and 1080px").
 *
 * The page differences Flutter has:
 *   - `align`: the login row centers the two columns vertically (Row crossAxisAlignment.center);
 *     the sign-up and MFA rows align them at the top
 *   - `fillRow`: the colour behind the row where the panel is shorter than the form — primaryColor
 *     on the sign-up page when onBoardingPage.footerText is set, and on the MFA page unless
 *     enableOnboardingBackgroundColor is false
 *   - `formColumnClassName`: the form column's own height and alignment
 *
 * Flutter source files:
 *   - presentation/web/auth/web_login/web_login_page.dart
 *   - presentation/web/auth/web_signup/web_signup_page.dart
 *   - presentation/web/auth/web_mfa_verification/web_mfa_verification_page.dart
 */

interface AuthLayoutProps {
  children: ReactNode;
  panelImage?: 'onBoarding' | 'signUp';
  /** Logo tap — Flutter WebOnboardingPage.onLogout; defaults to going home */
  onLogoClick?: () => void;
  align?: 'center' | 'start';
  fillRow?: boolean;
  formColumnClassName?: string;
}

export function AuthLayout({
  children,
  panelImage = 'onBoarding',
  onLogoClick,
  align = 'start',
  fillRow = false,
  formColumnClassName,
}: AuthLayoutProps) {
  const navigate = useNavigate();

  function handleLogoClick() {
    if (onLogoClick) {
      onLogoClick();
      return;
    }
    void analyticsService.logScreenView({ screenName: ROUTES.HOME });
    navigate(ROUTES.HOME, { replace: true });
  }

  return (
    <div className={styles.page}>
      <main
        className={[
          styles.row,
          align === 'center' ? styles.rowCenter : '',
          fillRow ? styles.rowFill : '',
        ].join(' ')}
      >
        <OnboardingPanel image={panelImage} onLogoClick={handleLogoClick} />
        <div className={`${styles.formColumn} ${formColumnClassName ?? ''}`}>{children}</div>
      </main>
      <AppFooter />
    </div>
  );
}
